import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

/**
 * Global Search Component
 * Search input with debounce for filtering grid data
 */
@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
  ],
  template: `
    <div class="global-search-container" [ngClass]="containerClass">
      <mat-form-field appearance="outline" class="w-full">
        <mat-label>{{ placeholder }}</mat-label>
        <input
          matInput
          [formControl]="searchControl"
          [placeholder]="placeholder"
          autocomplete="off"
          type="text">
        <mat-icon matPrefix class="text-gray-400">search</mat-icon>
        <button
          mat-icon-button
          matSuffix
          *ngIf="searchControl.value"
          (click)="onClear()"
          [attr.aria-label]="'Limpiar búsqueda'"
          type="button">
          <mat-icon class="text-gray-400">close</mat-icon>
        </button>
      </mat-form-field>

      <div *ngIf="showResultCount && totalResults !== null" class="result-count">
        <span class="text-sm text-gray-600 dark:text-gray-400">
          {{ totalResults }} {{ totalResults === 1 ? 'resultado' : 'resultados' }}
        </span>
      </div>
    </div>
  `,
  styles: [`
    .global-search-container {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
    }

    mat-form-field {
      margin: 0;
    }

    ::ng-deep .global-search-container {
      .mat-mdc-form-field {
        margin-bottom: 0 !important;
      }

      .mat-mdc-text-field-wrapper {
        padding-bottom: 0 !important;
      }

      .mat-mdc-form-field-subscript-wrapper {
        display: none;
      }
    }

    .result-count {
      white-space: nowrap;
      min-width: 100px;
      text-align: right;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  /**
   * Placeholder text for search input
   */
  @Input() placeholder: string = 'Buscar...';

  /**
   * Debounce time in milliseconds
   */
  @Input() debounceTime: number = 300;

  /**
   * Show result count
   */
  @Input() showResultCount: boolean = true;

  /**
   * Total results count (provided by parent)
   */
  @Input() totalResults: number | null = null;

  /**
   * Additional CSS classes for container
   */
  @Input() containerClass: string = '';

  /**
   * Initial search value
   */
  @Input() initialValue: string = '';

  /**
   * Emitted when search term changes (debounced)
   */
  @Output() searchChanged = new EventEmitter<string>();

  /**
   * Emitted when search is cleared
   */
  @Output() searchCleared = new EventEmitter<void>();

  // Form control for search input
  searchControl = new FormControl<string>('');

  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Set initial value if provided
    if (this.initialValue) {
      this.searchControl.setValue(this.initialValue, { emitEvent: false });
    }

    // Subscribe to value changes with debounce
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        const searchTerm = value?.trim() || '';
        this.searchChanged.emit(searchTerm);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Clear search input
   */
  onClear(): void {
    this.searchControl.setValue('', { emitEvent: true });
    this.searchCleared.emit();
  }

  /**
   * Set search value programmatically
   */
  setSearchValue(value: string): void {
    this.searchControl.setValue(value, { emitEvent: true });
  }

  /**
   * Get current search value
   */
  getSearchValue(): string {
    return this.searchControl.value?.trim() || '';
  }
}
